import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PricingCard } from "./PricingCard";
import { ArrowRight } from "lucide-react";
import { TeamPlanDialog } from "./TeamPlanDialog";
import { usePolarCheckout } from "@/hooks/usePolarCheckout";
import { POLAR_PRODUCTS } from "@/config/polar";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "@/context/LanguageContext";

export const PricingCards = ({ billingCycle }: { billingCycle: "monthly" | "yearly" }) => {
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const { startCheckout, loading } = usePolarCheckout();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleProCheckout = () => {
    if (!user) {
      navigate("/auth/signup");
      return;
    }
    startCheckout({
      productId: billingCycle === "yearly" ? POLAR_PRODUCTS.pro_yearly : POLAR_PRODUCTS.pro_monthly,
      plan: "pro",
      billingCycle,
    });
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
        <PricingCard
          tier={t("pricing.tierFree")}
          planKey="free"
          price={0}
          billingCycle={billingCycle}
          features={[
            t("pricing.freeFeature1"),
            t("pricing.freeFeature2"),
            t("pricing.freeFeature3"),
            t("pricing.freeFeature4"),
            t("pricing.freeFeature5"),
          ]}
          buttonText={t("pricing.getStarted")}
        />

        <PricingCard
          tier={t("pricing.tierPro")}
          planKey="pro"
          price={billingCycle === "yearly" ? 45 : 75}
          badge={t("pricing.popular")}
          billingCycle={billingCycle}
          features={[
            t("pricing.proFeature1"),
            t("pricing.proFeature2"),
            t("pricing.proFeature3"),
            t("pricing.proFeature4"),
            t("pricing.proFeature5"),
          ]}
          buttonText={loading ? t("pricing.loadingText") : t("pricing.startTrial7Days")}
          buttonIcon={loading ? undefined : ArrowRight}
          highlight
          onButtonClick={handleProCheckout}
        />

        <PricingCard
          tier={t("pricing.tierTeam")}
          planKey="team"
          price={billingCycle === "yearly" ? 34 : 56}
          billingCycle={billingCycle}
          isTeam={true}
          features={[
            { text: t("pricing.everythingInPro"), bold: true },
            { text: t("pricing.discountsScale") },
            { text: t("pricing.centralizedBilling"), bold: true },
            { text: t("pricing.sharedRooms") },
            { text: t("pricing.customPermissions") },
          ]}
          buttonText={t("pricing.chooseTeam")}
          buttonIcon={ArrowRight}
          onButtonClick={() => setTeamDialogOpen(true)}
        />
      </div>

      <TeamPlanDialog open={teamDialogOpen} onOpenChange={setTeamDialogOpen} />
    </>
  );
};
